import { useRef, useMemo, useEffect } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { addRequestInterceptor, addResponseInterceptor } from "@swiggy-private/native-bridge";
import Blaze from "@swiggy-private/blaze.js";
import { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge/dist/interfaces";
import BridgeSdk from "@swiggy-private/web-bridge-sdk";
import { filterHeadersFromSwiggyHeaders } from "helpers/newrelic";
import packageJson from "../package.json";
import { initBridgeSdk, isBrowserEnv } from "../utils/bridgeSdk";

// initialise Native Bridge SDK — BridgeSdk auto-detects Android/iOS/Web platform
export const Sdk = new BridgeSdk().sdk;

const EMPTY_FUNCTION = () => void 0;

const getPrefix = (prefix?: string): string => (!isEmpty(prefix) ? `${prefix}_` : "");

const createNBRequestInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    addRequestInterceptor((req) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}web_native_bridge_request`,
            id: req.requestId,
            type: req.type,
            subType: (req.payload as { state?: string })?.state ?? "",
        });
    });

const createNBResponseInterceptor = (recordNREvent?: Function, prefix?: string): VoidFunction =>
    addResponseInterceptor((res) => {
        if (recordNREvent == null) {
            return;
        }
        const projectPrefix = getPrefix(prefix);

        recordNREvent({
            eventType: `${projectPrefix}web_native_bridge_response`,
            id: res.requestId,
            type: res.type,
            statusCode: res.statusCode ?? -1,
            statusMessage: res.statusMessage ?? "",
            bridgeType: res.bridgeType ?? "",
        });
    });

export async function useBootstrap(
    nbEventTypePrefix: string,
    isCached?: boolean,
    swiggyHeaders?: GetSwiggyHeadersResponsePayload,
): Promise<void> {
    const initBridgeSdkRef = useRef(false);
    const initBlazeRef = useRef(false);

    if (!initBridgeSdkRef.current) {
        initBridgeSdk(Sdk ?? undefined);
        initBridgeSdkRef.current = true;
    }

    useEffect(() => {
        if (!initBlazeRef.current && !isEmpty(swiggyHeaders)) {
            Blaze.init({
                sendToAnalytics: EMPTY_FUNCTION,
                enableResourceTiming: false,
                nrConfig: {
                    enable: true,
                    config: {
                        NR_ENDPOINT: "https://insights-collector.newrelic.com/v1/accounts/737486/events", // enter newrelic endpoint here
                        NR_KEY: "CPDMQSXvjxGCdL_E02P9eZ-NJcEg-uGk", // enter newrelic key here
                    },
                    meta: {
                        appName: nbEventTypePrefix, // enter app name
                        /* JS hash is generated without replacing with actual value
                    So cached file would be same for both canary vs non-canary, and would have unknown behavior */
                        appDeployName: isBrowserEnv()
                            ? (document?.querySelector("[data-app-name]") as HTMLDivElement)?.dataset.appName ?? ""
                            : "",
                        ...(isCached && { isHtmlCached: String(isCached) }),
                        appVersion: packageJson.version,
                        ...(!isEmpty(swiggyHeaders) ? filterHeadersFromSwiggyHeaders(swiggyHeaders) : {}),
                    },
                },
            });
            initBlazeRef.current = true;
        }
    }, [isCached, nbEventTypePrefix, swiggyHeaders]);

    const requestInterceptor = useMemo(
        () => createNBRequestInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );
    const responseInterceptor = useMemo(
        () => createNBResponseInterceptor(Blaze.recordCustomEvent, nbEventTypePrefix),
        [nbEventTypePrefix],
    );

    useEffect(() => {
        return (): void => {
            requestInterceptor();
            responseInterceptor();
        };
    }, [requestInterceptor, responseInterceptor]);
}
